// src/slice/meetingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosClientSignIn } from '../axios/api.js';

// 🔹 Fetch all meetings
export const fetchMeetings = createAsyncThunk(
  'meetings/fetchMeetings',
  async (_, thunkAPI) => {
    const res = await axiosClientSignIn.get('/meetings');
    console.log(res.data);
    return res.data;
  }
);

// 🔹 Create a new meeting
export const createMeeting = createAsyncThunk(
  'meetings/createMeeting',
  async (meetingData, { rejectWithValue }) => {
    try {
      const res = await axiosClientSignIn.post('/meetings', meetingData);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

// 🔹 Update meeting
export const updateMeeting = createAsyncThunk(
  'meetings/updateMeeting',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const res = await axiosClientSignIn.put(`/meetings/${id}`, updates);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

// 🔹 Delete meeting
export const deleteMeeting = createAsyncThunk(
  'meetings/deleteMeeting',
  async (id, { rejectWithValue }) => {
    try {
      await axiosClientSignIn.delete(`/meetings/${id}`);
      return id;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

// 🔹 Update meeting status
export const updateMeetingStatus = createAsyncThunk(
  'meetings/updateMeetingStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await axiosClientSignIn.patch(`/meetings/${id}/status`, { status });
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

// 🔹 Get meeting by ID
export const fetchMeetingById = createAsyncThunk(
  'meetings/fetchMeetingById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosClientSignIn.get(`/meetings/${id}`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

// 🔹 Update attendee response (Accept/Decline)
export const updateAttendeeResponse = createAsyncThunk(
  'meetings/updateAttendeeResponse',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await axiosClientSignIn.patch(`/meetings/${id}/response`, { status });
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

// 🔹 Fetch completed meetings for archive
export const fetchMeetingsCompleted = createAsyncThunk(
  'meetings/fetchMeetingsCompleted',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosClientSignIn.get('/meetings/completed');
      console.log('Completed meetings:', res.data);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  }
);

const meetingSlice = createSlice({
  name: 'meetings',
  initialState: {
    meetings: [],
    meetingArchive: [], // Add archive for completed meetings
    selectedMeeting: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearSelectedMeeting: (state) => {
      state.selectedMeeting = null;
    },
    clearMeetingError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔄 Fetch Meetings
      .addCase(fetchMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.loading = false;
        // Only show non-completed meetings
        state.meetings = action.payload.filter(meeting => meeting.status !== 'completed');
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // 🔄 Fetch Completed Meetings for Archive
      .addCase(fetchMeetingsCompleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingsCompleted.fulfilled, (state, action) => {
        state.loading = false;
        state.meetingArchive = action.payload.filter(meeting => meeting.status === 'completed');
        console.log('Completed meetings in archive:', state.meetingArchive);
      })
      .addCase(fetchMeetingsCompleted.rejected, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.error = action.payload.message || 'Failed to fetch completed meetings';
        } else {
          state.error = action.error?.message || 'Failed to fetch completed meetings';
        }
      })

      // ➕ Create Meeting
      .addCase(createMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new meetings to the state
        if (action.payload.meetings) {
          state.meetings.push(...action.payload.meetings);
        }
      })
      .addCase(createMeeting.rejected, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.error = action.payload.message || action.payload.error || 'Unknown error';
        } else {
          state.error = action.error?.message || 'Failed to create meeting';
        }
      })

      // ✏️ Update Meeting
      .addCase(updateMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.meetings.findIndex(m => m._id === action.payload._id);
        if (index !== -1) {
          state.meetings[index] = action.payload;
        }
        if (state.selectedMeeting && state.selectedMeeting._id === action.payload._id) {
          state.selectedMeeting = action.payload;
        }
      })
      .addCase(updateMeeting.rejected, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.error = action.payload.message || 'Failed to update meeting';
        } else {
          state.error = action.error?.message || 'Failed to update meeting';
        }
      })

      // ❌ Delete Meeting
      .addCase(deleteMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = state.meetings.filter(m => m._id !== action.payload);
        if (state.selectedMeeting && state.selectedMeeting._id === action.payload) {
          state.selectedMeeting = null;
        }
      })
      .addCase(deleteMeeting.rejected, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.error = action.payload.message || 'Failed to delete meeting';
        } else {
          state.error = action.error?.message || 'Failed to delete meeting';
        }
      })

      // 🔄 Update Meeting Status
      .addCase(updateMeetingStatus.fulfilled, (state, action) => {
        const updatedMeeting = action.payload;
        
        // If meeting is completed, move from meetings to archive
        if (updatedMeeting.status === 'completed') {
          // Remove from active meetings
          state.meetings = state.meetings.filter(m => m._id !== updatedMeeting._id);
          // Add to archive if not already there
          const existsInArchive = state.meetingArchive.some(m => m._id === updatedMeeting._id);
          if (!existsInArchive) {
            state.meetingArchive.push(updatedMeeting);
          }
        } else {
          // If meeting is reactivated (pending), move from archive to active
          if (updatedMeeting.status === 'pending') {
            // Remove from archive
            state.meetingArchive = state.meetingArchive.filter(m => m._id !== updatedMeeting._id);
            // Add to active meetings if not already there
            const existsInMeetings = state.meetings.some(m => m._id === updatedMeeting._id);
            if (!existsInMeetings) {
              state.meetings.push(updatedMeeting);
            }
          } else {
            // Regular update for active meetings
            const index = state.meetings.findIndex(m => m._id === updatedMeeting._id);
            if (index !== -1) {
              state.meetings[index] = updatedMeeting;
            }
          }
        }
        
        // Update selected meeting
        if (state.selectedMeeting && state.selectedMeeting._id === updatedMeeting._id) {
          state.selectedMeeting = updatedMeeting;
        }
      })

      // 🔍 Fetch Meeting By ID
      .addCase(fetchMeetingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMeeting = action.payload;
      })
      .addCase(fetchMeetingById.rejected, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.error = action.payload.message || 'Failed to fetch meeting';
        } else {
          state.error = action.error?.message || 'Failed to fetch meeting';
        }
      })

      // 🔹 Update Attendee Response
      .addCase(updateAttendeeResponse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAttendeeResponse.fulfilled, (state, action) => {
        state.loading = false;
        // Update the meeting in the meetings array
        const index = state.meetings.findIndex(m => m._id === action.payload.meeting._id);
        if (index !== -1) {
          state.meetings[index] = action.payload.meeting;
        }
        // Update selected meeting if it matches
        if (state.selectedMeeting && state.selectedMeeting._id === action.payload.meeting._id) {
          state.selectedMeeting = action.payload.meeting;
        }
      })
      .addCase(updateAttendeeResponse.rejected, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.error = action.payload.message || 'Failed to update response';
        } else {
          state.error = action.error?.message || 'Failed to update response';
        }
      });
  },
});

export const { clearSelectedMeeting, clearMeetingError } = meetingSlice.actions;
export default meetingSlice.reducer;
